import React from "react";
import type { TrialCenterItem } from "types/trial";
import { trialService } from "services/systemadmin/trialService";
import SectionHeader from "components/ui/SectionHeader";
import StatusBadge from "components/ui/StatusBadge";
import ActionButton from "components/ui/ActionButton";
import EmptyState from "components/ui/EmptyState";
import { useLanguage } from "context/LanguageContext";
import {
  MdAccessTime,
  MdBlock,
  MdBusiness,
  MdCalendarToday,
  MdClose,
  MdLockOpen,
  MdNavigateBefore,
  MdNavigateNext,
  MdPerson,
  MdRefresh,
} from "react-icons/md";

type StatusFilter = "all" | "active" | "expiring_soon" | "expired";

const FILTER_TABS: { key: StatusFilter; labelKey: string }[] = [
  { key: "all", labelKey: "trial.filterAll" },
  { key: "active", labelKey: "trial.filterActive" },
  { key: "expiring_soon", labelKey: "trial.filterExpiringSoon" },
  { key: "expired", labelKey: "trial.filterExpired" },
];

function getStatusTone(item: TrialCenterItem): "success" | "warning" | "danger" | "neutral" {
  if (item.subscription_status === "TRIAL_READONLY") return "danger";
  if (item.is_expiring_soon) return "warning";
  if (item.subscription_status === "TRIAL") return "success";
  return "neutral";
}

function getStatusLabel(item: TrialCenterItem, t: (k: string) => string): string {
  if (item.subscription_status === "TRIAL_READONLY") return t("trial.statusExpired");
  if (item.is_expiring_soon) return t("trial.statusExpiringSoon");
  if (item.subscription_status === "TRIAL") return t("trial.statusActive");
  if (!item.subscription_status && !item.trial_end_date) return t("trial.statusNoTrial");
  return item.subscription_status || "-";
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return "-";
  return new Date(dateStr).toLocaleDateString("vi-VN", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
}

/** Inline date picker shown when admin clicks the calendar icon on a row. */
function DatePickerPopover({
  centerId,
  currentDate,
  onSave,
  onClose,
  saving,
}: {
  centerId: string;
  currentDate: string | null;
  onSave: (centerId: string, date: string) => void;
  onClose: () => void;
  saving: boolean;
}) {
  // Default to current trial_end_date or 30 days from now
  const defaultDate = currentDate
    ? new Date(currentDate).toISOString().slice(0, 10)
    : new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10);
  const [value, setValue] = React.useState(defaultDate);

  // Min date = tomorrow
  const minDate = new Date(Date.now() + 86400000).toISOString().slice(0, 10);

  return (
    <div className="absolute right-0 top-full z-20 mt-1 rounded-xl border border-line bg-surface p-3 shadow-lg">
      <div className="mb-2 flex items-center justify-between">
        <span className="text-xs font-semibold text-ink">
          Set trial end date
        </span>
        <button onClick={onClose} className="text-ink-subtle hover:text-gray-600">
          <MdClose className="h-4 w-4" />
        </button>
      </div>
      <input
        type="date"
        value={value}
        min={minDate}
        onChange={(e) => setValue(e.target.value)}
        className="w-full rounded-lg border border-line bg-surface px-3 py-1.5 text-sm text-ink outline-none focus:border-brand-500"
      />
      <div className="mt-2 flex justify-end gap-2">
        <button
          onClick={onClose}
          className="rounded-lg px-3 py-1 text-xs text-ink-muted hover:bg-surface-hover"
        >
          Cancel
        </button>
        <button
          disabled={saving || !value}
          onClick={() => onSave(centerId, value)}
          className="rounded-lg bg-brand-500 px-3 py-1 text-xs font-medium text-white hover:bg-brand-600 disabled:opacity-50"
        >
          {saving ? "..." : "Save"}
        </button>
      </div>
    </div>
  );
}

export default function TrialCenters() {
  const { t } = useLanguage();
  const [items, setItems] = React.useState<TrialCenterItem[]>([]);
  const [total, setTotal] = React.useState(0);
  const [loading, setLoading] = React.useState(true);
  const [filter, setFilter] = React.useState<StatusFilter>("all");
  const [pagination, setPagination] = React.useState({ page: 1, limit: 10 });
  const [extending, setExtending] = React.useState<string | null>(null);
  // Which row has the date picker open
  const [datePickerFor, setDatePickerFor] = React.useState<string | null>(null);

  const totalPages = Math.max(1, Math.ceil(total / pagination.limit));

  const fetchData = React.useCallback(async () => {
    setLoading(true);
    try {
      const skip = (pagination.page - 1) * pagination.limit;
      const res = await trialService.listTrialCenters({
        status_filter: filter === "all" ? undefined : filter,
        skip,
        limit: pagination.limit,
      });
      setItems(res.items);
      setTotal(res.total);
    } catch (err) {
      console.error("Failed to load trial centers:", err);
    } finally {
      setLoading(false);
    }
  }, [filter, pagination]);

  React.useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Reset to page 1 when filter changes
  React.useEffect(() => {
    if (pagination.page !== 1) {
      setPagination((p) => ({ ...p, page: 1 }));
    }
  }, [filter]);

  const handleExtend = async (centerId: string, days: number) => {
    setExtending(centerId);
    try {
      await trialService.extendCenterTrial(centerId, days);
      await fetchData();
    } catch (err) {
      console.error("Failed to extend trial:", err);
    } finally {
      setExtending(null);
    }
  };

  const handleSetCustomDate = async (centerId: string, dateStr: string) => {
    setExtending(centerId);
    try {
      // Convert local date to UTC ISO string (end of that day)
      const endDate = new Date(dateStr + "T23:59:59Z").toISOString();
      await trialService.updateCenterTrial(centerId, {
        trial_end_date: endDate,
        subscription_status: "TRIAL",
      });
      setDatePickerFor(null);
      await fetchData();
    } catch (err) {
      console.error("Failed to set custom date:", err);
    } finally {
      setExtending(null);
    }
  };

  /** Force a center into read-only mode immediately. */
  const handleSetExpired = async (centerId: string) => {
    if (!window.confirm(t("trial.confirmExpire"))) return;
    setExtending(centerId);
    try {
      await trialService.updateCenterTrial(centerId, {
        subscription_status: "TRIAL_READONLY",
      });
      await fetchData();
    } catch (err) {
      console.error("Failed to set expired:", err);
    } finally {
      setExtending(null);
    }
  };

  /** Reactivate: lift read-only, keep trial_end_date, set status back to TRIAL. */
  const handleReactivate = async (centerId: string) => {
    setExtending(centerId);
    try {
      await trialService.updateCenterTrial(centerId, {
        subscription_status: "TRIAL",
      });
      await fetchData();
    } catch (err) {
      console.error("Failed to reactivate:", err);
    } finally {
      setExtending(null);
    }
  };

  /** Reset: remove trial entirely, restore full unrestricted access. */
  const handleReset = async (centerId: string) => {
    if (!window.confirm(t("trial.confirmReset"))) return;
    setExtending(centerId);
    try {
      await trialService.updateCenterTrial(centerId, {
        trial_end_date: null,
        subscription_status: null,
      });
      await fetchData();
    } catch (err) {
      console.error("Failed to reset trial:", err);
    } finally {
      setExtending(null);
    }
  };

  return (
    <div className="mt-5 px-4">
      <SectionHeader
        title={t("trial.title")}
        description={t("trial.description")}
        icon={<MdAccessTime className="h-5 w-5" />}
        actions={
          <ActionButton
            startIcon={<MdRefresh className="h-5 w-5" />}
            onClick={fetchData}
            variant="ghost"
          >
            {t("trial.refresh")}
          </ActionButton>
        }
      />

      {/* Filter Tabs */}
      <div className="mb-4 flex flex-wrap gap-2">
        {FILTER_TABS.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setFilter(tab.key)}
            className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
              filter === tab.key
                ? "bg-brand-500 text-white"
                : "bg-surface-muted text-gray-600 hover:bg-surface-hover dark:text-gray-300"
            }`}
          >
            {t(tab.labelKey)}
          </button>
        ))}
        {!loading && (
          <span className="flex items-center text-sm text-ink-muted">
            ({total} {t("trial.centersFound")})
          </span>
        )}
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center py-24">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-brand-500 border-t-transparent" />
        </div>
      ) : items.length === 0 ? (
        <EmptyState
          icon={<MdBusiness className="h-14 w-14 opacity-70" />}
          title={t("trial.emptyTitle")}
          description={t("trial.emptyDescription")}
        />
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-line bg-surface shadow-sm">
          <table className="w-full min-w-[750px] text-left text-sm">
            <thead>
              <tr className="border-b border-line">
                <th className="px-4 py-3 font-semibold text-gray-600 dark:text-gray-300">
                  {t("trial.colCenter")}
                </th>
                <th className="px-4 py-3 font-semibold text-gray-600 dark:text-gray-300">
                  {t("trial.colManager")}
                </th>
                <th className="px-4 py-3 font-semibold text-gray-600 dark:text-gray-300">
                  {t("trial.colTrialEnd")}
                </th>
                <th className="px-4 py-3 font-semibold text-gray-600 dark:text-gray-300">
                  {t("trial.colDaysRemaining")}
                </th>
                <th className="px-4 py-3 font-semibold text-gray-600 dark:text-gray-300">
                  {t("trial.colUsers")}
                </th>
                <th className="px-4 py-3 font-semibold text-gray-600 dark:text-gray-300">
                  {t("trial.colStatus")}
                </th>
                <th className="px-4 py-3 font-semibold text-gray-600 dark:text-gray-300">
                  {t("trial.colActions")}
                </th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr
                  key={item.id}
                  className="border-b border-gray-50 last:border-0 hover:bg-gray-50 dark:border-line dark:hover:bg-surface-hover/40"
                >
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <MdBusiness className="h-4 w-4 shrink-0 text-brand-500" />
                      <span className="font-medium text-ink">
                        {item.name}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    {item.manager_name ? (
                      <div className="flex items-center gap-2">
                        <MdPerson className="h-4 w-4 shrink-0 text-purple-500" />
                        <div>
                          <p className="text-ink">
                            {item.manager_name}
                          </p>
                          <p className="text-xs text-ink-subtle">
                            {item.manager_email}
                          </p>
                        </div>
                      </div>
                    ) : (
                      <span className="text-ink-subtle">-</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-navy-700 dark:text-gray-300">
                    {formatDate(item.trial_end_date)}
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`font-semibold ${
                        item.days_remaining === 0
                          ? "text-red-500"
                          : item.is_expiring_soon
                          ? "text-amber-500"
                          : "text-ink"
                      }`}
                    >
                      {item.days_remaining ?? "-"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-navy-700 dark:text-gray-300">
                    {item.total_users}
                  </td>
                  <td className="px-4 py-3">
                    <StatusBadge
                      label={getStatusLabel(item, t)}
                      tone={getStatusTone(item)}
                    />
                  </td>
                  <td className="px-4 py-3">
                    <div className="relative flex flex-wrap items-center gap-1">
                      {/* Quick extend buttons */}
                      {[7, 30, 90].map((days) => (
                        <button
                          key={days}
                          disabled={extending === item.id}
                          onClick={() => handleExtend(item.id, days)}
                          className="rounded-lg border border-line px-2 py-1 text-xs font-medium text-brand-600 transition-colors hover:bg-brand-50 disabled:opacity-50 dark:text-brand-300 dark:hover:bg-brand-500/10"
                        >
                          {extending === item.id ? "..." : `+${days}d`}
                        </button>
                      ))}
                      {/* Custom date button */}
                      <button
                        onClick={() =>
                          setDatePickerFor(datePickerFor === item.id ? null : item.id)
                        }
                        title={t("trial.setCustomDate")}
                        className="rounded-lg border border-line px-2 py-1 text-xs text-ink-muted transition-colors hover:bg-surface-hover"
                      >
                        <MdCalendarToday className="h-3.5 w-3.5" />
                      </button>
                      {/* Set expired (only if not already expired) */}
                      {item.subscription_status !== "TRIAL_READONLY" && (
                        <button
                          disabled={extending === item.id}
                          onClick={() => handleSetExpired(item.id)}
                          title={t("trial.setExpired")}
                          className="rounded-lg border border-red-200 px-2 py-1 text-xs text-red-500 transition-colors hover:bg-red-50 disabled:opacity-50 dark:border-red-900/40 dark:text-red-400 dark:hover:bg-red-900/20"
                        >
                          <MdBlock className="h-3.5 w-3.5" />
                        </button>
                      )}
                      {/* Reactivate (only if currently expired) */}
                      {item.subscription_status === "TRIAL_READONLY" && (
                        <button
                          disabled={extending === item.id}
                          onClick={() => handleReactivate(item.id)}
                          title={t("trial.reactivate")}
                          className="rounded-lg border border-green-200 px-2 py-1 text-xs text-green-600 transition-colors hover:bg-green-50 disabled:opacity-50 dark:border-green-900/40 dark:text-green-400 dark:hover:bg-green-900/20"
                        >
                          <MdLockOpen className="h-3.5 w-3.5" />
                        </button>
                      )}
                      {/* Reset: remove trial entirely (only if center has a trial) */}
                      {(item.trial_end_date || item.subscription_status) && (
                        <button
                          disabled={extending === item.id}
                          onClick={() => handleReset(item.id)}
                          title={t("trial.resetTrial")}
                          className="rounded-lg border border-line px-2 py-1 text-xs text-ink-muted transition-colors hover:bg-surface-hover disabled:opacity-50"
                        >
                          <MdClose className="h-3.5 w-3.5" />
                        </button>
                      )}
                      {/* Date picker popover */}
                      {datePickerFor === item.id && (
                        <DatePickerPopover
                          centerId={item.id}
                          currentDate={item.trial_end_date}
                          onSave={handleSetCustomDate}
                          onClose={() => setDatePickerFor(null)}
                          saving={extending === item.id}
                        />
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination Controls */}
      {!loading && items.length > 0 && (
        <div className="mt-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-ink">
              {t("trial.rowsPerPage")}:
            </label>
            <select
              value={pagination.limit}
              onChange={(e) => {
                const newLimit = parseInt(e.target.value, 10);
                setPagination({ page: 1, limit: newLimit });
              }}
              className="rounded-lg border border-line bg-surface px-3 py-2 text-sm outline-none dark:text-white"
            >
              <option value="10">10</option>
              <option value="25">25</option>
              <option value="50">50</option>
              <option value="100">100</option>
            </select>
          </div>

          <div className="flex items-center gap-3">
            <div className="flex flex-col items-end gap-1">
              <span className="text-xs text-ink-muted">
                {total} {t("trial.totalCenters")}
              </span>
              <span className="text-sm text-gray-600 dark:text-gray-400">
                {total > 0
                  ? `${((pagination.page - 1) * pagination.limit + 1).toLocaleString()} - ${Math.min(pagination.page * pagination.limit, total).toLocaleString()}`
                  : "0"}
              </span>
            </div>

            <button
              type="button"
              onClick={() => setPagination((p) => ({ ...p, page: Math.max(1, p.page - 1) }))}
              disabled={pagination.page === 1}
              className="flex items-center gap-1 rounded-lg border border-line px-3 py-2 text-sm font-medium text-gray-600 hover:bg-surface-hover disabled:opacity-50 disabled:cursor-not-allowed dark:text-gray-300"
            >
              <MdNavigateBefore className="h-4 w-4" />
              {t("trial.prev")}
            </button>

            <span className="text-sm font-medium text-ink-muted min-w-[120px] text-center">
              {t("trial.page")} {pagination.page} / {totalPages}
            </span>

            <button
              type="button"
              onClick={() => setPagination((p) => ({ ...p, page: p.page + 1 }))}
              disabled={pagination.page >= totalPages}
              className="flex items-center gap-1 rounded-lg border border-line px-3 py-2 text-sm font-medium text-gray-600 hover:bg-surface-hover disabled:opacity-50 disabled:cursor-not-allowed dark:text-gray-300"
            >
              {t("trial.next")}
              <MdNavigateNext className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
